// Клиент API пакета 8 «50 улучшений SMS-центра»: «Главная» партнёра, глобальный поиск,
// разбор лучших кампаний сети и охват согласий. Типы повторяют ответы Go (go/retention:
// partner_digest.go, partner_home.go, global_search.go, learning_cases.go, consent_coverage.go).
import { apiDelete, apiGet, apiPatch, apiPost } from "../api";

// ───────────── «Главная» партнёра ─────────────

export interface HomeWeek {
  from: string;
  to: string;
  label: string;
}

export interface HomeLift {
  campaigns: number;
  exposedRuns: number;
  holdoutRuns: number;
  exposedShare: number | null;
  holdoutShare: number | null;
  /** Разница в процентных пунктах; null — выборка мала. */
  diffPp: number | null;
  note: string;
}

export interface HomeBalance {
  balance: string;
  reserved: string;
  spendable: string;
  spent28d: string;
  /** null — расхода за 28 дней не было, оценить нечем. */
  daysLeft: number | null;
}

export interface HomeSuggestion {
  id: string;
  name: string;
  goal: string;
  description: string;
  sampleText: string;
  path: string;
}

export interface HomeLastCampaign {
  id: string;
  name: string;
  status: string;
  lastSentAt: string | null;
  messages: number | null;
  orders: number | null;
  /** null — нет результата или нет права на финансы. */
  revenue: string | null;
  windowDays: number;
  resultPath: string;
}

export interface PartnerHome {
  week: HomeWeek;
  partnerId?: string;
  partnerName?: string;
  active: boolean;
  messages: number;
  channels: Record<string, number>;
  delivered: number;
  /** null — нет права на финансы. */
  spent: string | null;
  ordersAfter: number;
  /** null — нет права на финансы: выручка, как и расходы, только с retention:billing; остаётся число заказов. */
  revenueAfter: string | null;
  attributionDays: number;
  lift: HomeLift | null;
  balance: HomeBalance | null;
  suggestions: HomeSuggestion[];
  lastCampaign?: HomeLastCampaign | null;
  canViewMoney: boolean;
  link: string;
  partial?: boolean;
  canLaunch: boolean;
  canTopUp: boolean;
  noPoints: boolean;
}

// ───────────── Глобальный поиск ─────────────

export interface SearchCampaign {
  id: string;
  name: string;
  status: string;
  path: string;
}
export interface SearchGuest {
  id: string;
  name: string;
  phone: string;
  city: string;
  path: string;
}
export interface SearchPromo {
  code: string;
  status: "available" | "assigned";
  pool: string;
  path: string;
}
export interface SearchTemplate {
  id: string;
  name: string;
  channel: string;
  builtin: boolean;
  path: string;
}
export interface SearchResponse {
  campaigns: SearchCampaign[];
  guests: SearchGuest[];
  promos: SearchPromo[];
  templates: SearchTemplate[];
  phonesMasked: boolean;
  partial?: boolean;
}

// ───────────── Разбор лучших кампаний ─────────────

export interface CaseFigure {
  label: string;
  value: string;
}
export interface LearningCase {
  id: string;
  title: string;
  body: string;
  figures: CaseFigure[];
  sortOrder: number;
  published: boolean;
  updatedAt: string;
}
export type LearningCaseDraft = Pick<LearningCase, "title" | "body" | "figures" | "sortOrder" | "published">;
export interface LearningCasesResponse {
  cases: LearningCase[];
  canEdit: boolean;
}

// ───────────── Охват согласий ─────────────

export interface ConsentCounts {
  guests: number;
  sms: number;
  email: number;
  push: number;
  any: number;
}
export type ConsentChannel = "sms" | "email" | "push" | "any";
export interface ConsentPoint extends ConsentCounts {
  pointId: string;
  name: string;
  city: string;
  tenantId: string;
  companyId: string;
  company: string;
  prev: (ConsentCounts & { date: string }) | null;
}
export interface ConsentCity extends ConsentCounts {
  tenantId: string;
  city: string;
  points: number;
}
export interface ConsentChannelRow {
  channel: ConsentChannel;
  label: string;
  consented: number;
  share: number | null;
  prevShare: number | null;
  shareDelta: number | null;
}
export interface ConsentTrendDay extends ConsentCounts {
  date: string;
  smsShare: number | null;
  emailShare: number | null;
  pushShare: number | null;
  anyShare: number | null;
}
export interface ConsentCoverage {
  available: boolean;
  asOf: string;
  windowDays: number;
  trendDays: number;
  totals: ConsentCounts;
  channels: ConsentChannelRow[];
  cities: ConsentCity[];
  points: ConsentPoint[];
  lowCoverage: { channel: ConsentChannel; threshold: number; minGuests: number; items: ConsentPoint[] };
  trend: ConsentTrendDay[];
  caveats: string[];
}
export interface ConsentParams {
  tenant?: string;
  company?: string;
  /** Единый фильтр «Город → ИП → Точки» (пакет 9b): id точек через запятую. */
  pointIds?: string;
  days?: number;
  channel?: ConsentChannel;
  threshold?: number;
}

export const hubKeys = {
  home: ["hub", "home"] as const,
  search: (query: string) => ["hub", "search", query] as const,
  cases: ["hub", "learning-cases"] as const,
  consents: (params: ConsentParams) => ["hub", "consents", params] as const,
};

export function consentQuery(params: ConsentParams): string {
  const q = new URLSearchParams();
  if (params.tenant) q.set("tenant", params.tenant);
  if (params.company) q.set("company", params.company);
  if (params.pointIds) q.set("pointIds", params.pointIds);
  if (params.days) q.set("days", String(params.days));
  if (params.channel) q.set("channel", params.channel);
  if (params.threshold) q.set("threshold", String(params.threshold));
  return q.size ? `?${q}` : "";
}

export const hubApi = {
  home: (signal?: AbortSignal) => apiGet<PartnerHome>("/api/partner/home", signal),
  search: (query: string, signal?: AbortSignal) =>
    apiGet<SearchResponse>(`/api/search?q=${encodeURIComponent(query)}`, signal),
  learningCases: (signal?: AbortSignal) => apiGet<LearningCasesResponse>("/api/learning/cases", signal),
  createCase: (draft: LearningCaseDraft) => apiPost<LearningCase>("/api/learning/cases", draft),
  updateCase: (id: string, draft: LearningCaseDraft) =>
    apiPatch<LearningCase>(`/api/learning/cases/${encodeURIComponent(id)}`, draft),
  deleteCase: (id: string) => apiDelete<{ ok: boolean }>(`/api/learning/cases/${encodeURIComponent(id)}`),
  consentCoverage: (params: ConsentParams, signal?: AbortSignal) =>
    apiGet<ConsentCoverage>(`/api/consents/coverage${consentQuery(params)}`, signal),
};
